import React, { useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Navbar, Container, Nav } from 'react-bootstrap';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './App.css';
import Forum from './components/Forum';
import Application from './components/Application';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';

// Import images
// import logo from './assets/logo.png';
// import aboutImage from './assets/2.jpg';
// import creatorImage from './assets/3.png';
// import departmentsImage from './assets/4.png';

function Home({ audioRef, volume, isMuted, handleVolumeChange, toggleMute }) {
  return (
    <>
      {/* Background Music */}
      <audio
        ref={audioRef}
        src="/HighBeams.wav"
        autoPlay
        loop
        style={{ display: 'none' }}
        volume={volume}
      />
      
      {/* Header */}
      <header className="masthead">
        <Container className="d-flex h-100 align-items-center">
          <div className="mx-auto text-center">
            <div className="logo">
              <img className='transparency' src="logo.png" alt="SkyLine RP Logo" />
            </div>
            <div className="mt-4">
              <Link to="/forum" className="btn btn-primary">
                <i className="fas fa-play-circle me-2"></i>
                Get Started
              </Link>
            </div>
          </div>
        </Container>
      </header>

      {/* Info Section */}
      <section className="projects-section" id="info">
        <Container>

          {/* About Us Row */}
          <div className="row justify-content-center no-gutters mb-5 mb-lg-0">
            <div className="col-lg-6">
              <img className="img-fluid" src="LCstreet.jpg" alt="Departments Information" />
            </div>
            <div className="col-lg-6 order-lg-first">
              <div className="project">
                <div className="project-text">
                  <h4>About Us</h4>
                  <p>
                  Welcome to SkyLine RP — an immersive 16+ FiveM roleplaying community where your story is yours to create. Dive into a world where you shape your characters, forge your own path, and live out your vision of roleplay—your way.
                  Whether you're a seasoned RPer or just starting out, SkyLine RP offers a space to explore limitless possibilities, one scene at a time.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Project One Row */}
          <div className="row justify-content-center no-gutters mb-5 mb-lg-0">
            <div className="col-lg-6">
              <img className="img-fluid" src="S_cars.jpg" alt="Creator Information" />
            </div>
            <div className="col-lg-6">
              <div className="project">
                <div className="project-text">
                  <h4>Be a Creator</h4>
                  <p>
                  At SkyLine RP, we believe in unrestricted creativity. Stream, record, and share your journey freely—no approvals, no media tag applications. 
                  Whether you're building your brand or just love creating content, you're empowered to grow your audience and tell your story without limits.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Project Two Row */}
          <div className="row justify-content-center no-gutters">
            <div className="col-lg-6">
              <img className="img-fluid" src="smallhero.jpeg" alt="Departments Information" />
            </div>
            <div className="col-lg-6 order-lg-first">
              <div className="project">
                <div className="project-text">
                  <h4>Departments</h4>
                  <p>
                  When you join SkyLine RP, you'll start your journey as a civilian, with the opportunity to explore roles like Fire/EMS and Dispatcher right away.
                   Law Enforcement (LEO) positions become available after completing the necessary training and requirements, ensuring a realistic and structured path for those looking to serve and protect.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Apply Section */}
      <section className="apply-section" id="apply">
        <Container>
          <div className="row">
            <div className="col-md-10 col-lg-8 mx-auto text-center">
              <form className="form-inline d-flex">
                <Link to="/forum" className="btn btn-primary mx-auto" style={{ marginTop: '20px' }}>
                  Apply on our Forum!
                </Link>
              </form>
            </div>
          </div>
        </Container>
      </section>

      {/* Contact Section */}
      <section className="contact-section bg-black">
        <Container>
          <div className="social d-flex justify-content-center">
            {/* eslint-disable-next-line */}
            <a href="#" className="mx-2 social-icon">
              <i className="fab fa-twitter fa-2x"></i>
            </a>
            {/* eslint-disable-next-line */}
            <a href="#" className="mx-2 social-icon">
              <i className="fab fa-facebook fa-2x"></i>
            </a>
            {/* eslint-disable-next-line */}
            <a href="#" className="mx-2 social-icon">
              <i className="fab fa-instagram fa-2x"></i>
            </a>
            {/* eslint-disable-next-line */}
            <a href="#" className="mx-2 social-icon">
              <i className="fab fa-youtube fa-2x"></i>
            </a>
            {/* eslint-disable-next-line */}
            <a href="#" className="mx-2 social-icon">
              <i className="fab fa-tiktok fa-2x"></i>
            </a>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <footer className="bg-black small text-center text-white-50">
        <Container>
          Copyright © www.skyline-rp.org {new Date().getFullYear()}
        </Container>
      </footer>
    </>
  );
}

function App() {
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.2);
  const audioRef = useRef(null);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // === SITE UNAVAILABLE MODE ===
  // To restore the site, comment out the return below and uncomment the main app return.
  // return (
  //   <Unavailable />
  // );
  // === END SITE UNAVAILABLE MODE ===

  // Main app logic (uncomment to restore site)
  return (
    <Router>
      <Navbar expand="lg" fixed="top">
        <Container>
          <Navbar.Brand href="/">  
            <img
              src="/logo.png"
              alt="SkyLine RP"
              height="40"
              className="d-inline-block align-top"
            />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto">
              <Nav.Link href="/">Home</Nav.Link>
              <Nav.Link href="/forum">Forum</Nav.Link>
              {/* <Nav.Link href="/admin/login" style={{ fontSize: '0.2rem', opacity: 0.0 }}>Admin</Nav.Link> */}
              <div className="d-flex align-items-center ms-3">
                <button 
                  onClick={toggleMute} 
                  className="btn btn-link text-white"
                  style={{ padding: '0.5rem' }}
                >
                  <i className={`fas fa-volume-${isMuted ? 'mute' : 'up'}`}></i>
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="form-range ms-2"
                  style={{ width: '100px' }}
                />
              </div>
              <Nav.Link 
                href="https://discord.gg/ZX4fkY2TKV" 
                target="_blank" 
                rel="noopener noreferrer"
                className="discord-btn"
              >
                <i className="fab fa-discord me-2"></i>
                Join Discord
              </Nav.Link>
              <Nav.Link href="/admin/login" style={{ fontSize: '1rem', opacity: 0.05 }}>Admin</Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Routes>
        <Route path="/" element={<Home 
          audioRef={audioRef}
          volume={volume}
          isMuted={isMuted}
          handleVolumeChange={handleVolumeChange}
          toggleMute={toggleMute}
        />} />
        <Route path="/forum" element={<Forum />} />
        <Route path="/application" element={<Application />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
      </Routes>
      <SpeedInsights />
      <Analytics />
    </Router>
  );
}

export default App;
